/**
 * Conversion between AdapterCdoOpcUa (UI form state) and OpcUa.MappingT
 * (FlatBuffer Object API) for binary save/load — modeled on
 * `utils/modbusFlatBuffer.ts`.
 *
 * OPC UA CDO config is the generated `OpcUa.Mapping` — union-heavy
 * (Identity, NodeIdentifier, Data/Variant, MonitorMode, FilterOperand), so
 * it CANNOT go through the JSON `cdo_input` path. Instead: build
 * `OpcUa.MappingT` via the generated Object API, `.pack()` to bytes,
 * base64-encode, and send as `cdo_binary` on create (with
 * `cdo_schema_name: "opc_ua"`) and via `PUT /adapters/:id/cdo` on update;
 * read it back by decoding the adapter's `cdoBinary`.
 *
 * Union pattern used throughout (mirrors `editorIdentityToFb` below):
 * construct the concrete member `*T`, return it alongside the matching
 * `xxxType` discriminant enum value — no wrapper constructor exists on the
 * generated union types. Decoding: `unpack()` already resolves the union
 * member via the generated `unionToXxx` accessor, so the decoder only
 * needs to branch on the sibling `xxxType` field to know which concrete
 * type `t.xxx` is at runtime.
 */
import * as flatbuffers from "flatbuffers";

import { Mapping, MappingT } from "../generated/opc-ua/mapping";
import { AnonymousIdentityT } from "../generated/opc-ua/anonymous-identity";
import { UserNameIdentityT } from "../generated/opc-ua/user-name-identity";
import { X509IdentityT } from "../generated/opc-ua/x509-identity";
import { Identity } from "../generated/opc-ua/identity";
import { CertificateConfigT } from "../generated/opc-ua/certificate-config";
import { OpcUaMappingsT } from "../generated/opc-ua/opc-ua-mappings";
import { NodeValueToMessageEntryT } from "../generated/opc-ua/node-value-to-message-entry";
import { MessageToNodeValueEntryT } from "../generated/opc-ua/message-to-node-value-entry";
import { NodeToMsgTemplateT } from "../generated/opc-ua/node-to-msg-template";
import { MsgToNodeTemplateT } from "../generated/opc-ua/msg-to-node-template";
import { FloorExpansionT } from "../generated/opc-ua/floor-expansion";
import { FloorExpansionMode } from "../generated/opc-ua/floor-expansion-mode";
import { NodeValueT } from "../generated/opc-ua/node-value";
import { NodeIdT } from "../generated/opc-ua/node-id";
import { NodeIdentifier } from "../generated/opc-ua/node-identifier";
import { NumericNodeIdT } from "../generated/opc-ua/numeric-node-id";
import { StringNodeIdT } from "../generated/opc-ua/string-node-id";
import { GuidNodeIdT } from "../generated/opc-ua/guid-node-id";
import { ByteStringNodeIdT } from "../generated/opc-ua/byte-string-node-id";
import { BrowsePathT } from "../generated/opc-ua/browse-path";
import { RelativePathElementT } from "../generated/opc-ua/relative-path-element";
import { QualifiedNameT } from "../generated/opc-ua/qualified-name";
import { Data } from "../generated/opc-ua/data";
import { DataHolderT } from "../generated/opc-ua/data-holder";
import { ArrayT } from "../generated/opc-ua/array";
import { BoolT } from "../generated/opc-ua/bool";
import { SByteT } from "../generated/opc-ua/sbyte";
import { ByteT } from "../generated/opc-ua/byte";
import { U16T } from "../generated/opc-ua/u16";
import { I16T } from "../generated/opc-ua/i16";
import { U32T } from "../generated/opc-ua/u32";
import { I32T } from "../generated/opc-ua/i32";
import { U64T } from "../generated/opc-ua/u64";
import { I64T } from "../generated/opc-ua/i64";
import { FloatT } from "../generated/opc-ua/float";
import { DoubleT } from "../generated/opc-ua/double";
import { StringT } from "../generated/opc-ua/string";
import { DateTimeT } from "../generated/opc-ua/date-time";
import { GuidT } from "../generated/opc-ua/guid";
import { ByteStringT } from "../generated/opc-ua/byte-string";
import { StatusCodeValueT } from "../generated/opc-ua/status-code-value";
import { LocalizedTextT } from "../generated/opc-ua/localized-text";
import { QualifiedNameValueT } from "../generated/opc-ua/qualified-name-value";
import { NodeIdValueT } from "../generated/opc-ua/node-id-value";
import { ExpandedNodeIdValueT } from "../generated/opc-ua/expanded-node-id-value";
import { XmlElementT } from "../generated/opc-ua/xml-element";
import { AttributeId } from "../generated/opc-ua/attribute-id";
import { SessionConfigT } from "../generated/opc-ua/session-config";
import { RedundancyConfigT } from "../generated/opc-ua/redundancy-config";
import { RedundancySupport } from "../generated/opc-ua/redundancy-support";
import { SubscriptionConfigT } from "../generated/opc-ua/subscription-config";
import { NamedSubscriptionT } from "../generated/opc-ua/named-subscription";
import { NodeMonitorT } from "../generated/opc-ua/node-monitor";
import { MonitorMode } from "../generated/opc-ua/monitor-mode";
import { SubscribeModeT } from "../generated/opc-ua/subscribe-mode";
import { PollModeT } from "../generated/opc-ua/poll-mode";
import { MonitoringParametersT } from "../generated/opc-ua/monitoring-parameters";
import { MessageToMethodEntryT } from "../generated/opc-ua/message-to-method-entry";
import { MethodCallT } from "../generated/opc-ua/method-call";
import { EventMonitorT } from "../generated/opc-ua/event-monitor";
import { EventFilterT } from "../generated/opc-ua/event-filter";
import { SimpleAttributeOperandT } from "../generated/opc-ua/simple-attribute-operand";
import { ContentFilterT } from "../generated/opc-ua/content-filter";
import { ContentFilterElementT } from "../generated/opc-ua/content-filter-element";
import { FilterOperandHolderT } from "../generated/opc-ua/filter-operand-holder";
import { FilterOperand } from "../generated/opc-ua/filter-operand";
import { LiteralOperandT } from "../generated/opc-ua/literal-operand";
import { ElementOperandT } from "../generated/opc-ua/element-operand";
import { EventToMessageEntryT } from "../generated/opc-ua/event-to-message-entry";
import { HistoryReadConfigT } from "../generated/opc-ua/history-read-config";

import type {
  AdapterCdoOpcUa,
  OpcUaNodeIdForm,
  OpcUaRelativePathHopForm,
  OpcUaIdentityForm,
  OpcUaCertificateConfigForm,
  OpcUaDataValueForm,
  OpcUaDataKind,
  OpcUaNodeToMessageRow,
  OpcUaMessageToNodeRow,
  OpcUaFloorExpansionForm,
  OpcUaSessionForm,
  OpcUaRedundancyForm,
  OpcUaSubscriptionConfigForm,
  OpcUaNamedSubscriptionForm,
  OpcUaMonitorModeForm,
  OpcUaMonitoringParametersForm,
  OpcUaNodeMonitorForm,
  OpcUaMethodMappingForm,
  OpcUaSimpleAttributeOperandForm,
  OpcUaFilterOperandForm,
  OpcUaContentFilterElementForm,
  OpcUaEventMonitorForm,
  OpcUaEventToMessageRow,
  OpcUaHistoryReadForm,
} from "../../interfaces/AdapterCdoOpcUa.interface";
import { Value } from "../generated/value/value";
import { IntValueT } from "../generated/value/int-value";
import { ValuedMessageT } from "../generated/ln2/valued-message";
import { MessageContent } from "../generated/ln2/message-content";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";
import { TrafficMessageT } from "../generated/traffic/traffic-message";
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { ControlMessageT } from "../generated/control/control-message";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import {
  compositeKeyToValuedMessageT,
  valuedMessageTToCompositeKey,
} from "../types/valuedMessageBuilder";
import { extractPositionFromPayload } from "./channelModifierPayloadBuilder";
import { FLOOR_PLACEHOLDER } from "../../pages/edgeManagement/adapters/CDO/OPCUA/floorRange";

// =====================================================================
// Shared string/binary decode helpers (avoid the String/Array global
// shadow from data.ts/array.ts — never call the global String()/Array()
// constructors in this file; use template literals and [] literals).
// =====================================================================

function asDecodedString(v: string | Uint8Array | null | undefined): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  return new TextDecoder().decode(v);
}

// =====================================================================
// NodeId <-> Editor (5-kind NodeIdentifier union)
// =====================================================================

/** Parse a "ns;identifier" numeric-node-id shorthand string used by nested value pickers. */
function parseNsIdentifier(raw: string): { namespaceIndex: number; identifier: string } {
  const parts = raw.split(";");
  const ns = parts.length > 1 ? Number(parts[0]) : 0;
  const identifier = parts.length > 1 ? parts[1] : (parts[0] ?? "");
  return { namespaceIndex: Number.isFinite(ns) ? ns : 0, identifier };
}

function formatNsIdentifier(namespaceIndex: number, identifier: string): string {
  return `${namespaceIndex};${identifier}`;
}

/** Convert a comma-separated decimal-byte string ("1,2,255") to number[]. */
function parseByteList(raw: string): number[] {
  if (raw.trim() === "") return [];
  return raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
}

function formatByteList(bytes: readonly number[]): string {
  return bytes.join(",");
}

function editorHopToFb(hop: OpcUaRelativePathHopForm): RelativePathElementT {
  return new RelativePathElementT(
    hop.referenceTypeNode ? editorNodeIdToFb(hop.referenceTypeNode) : null,
    hop.isInverse,
    hop.includeSubtypes,
    new QualifiedNameT(hop.targetNamespaceIndex, hop.targetName),
  );
}

/** Convert an OpcUaNodeIdForm to a NodeIdT — dispatches across all 5 NodeIdentifier kinds. */
function editorNodeIdToFb(node: OpcUaNodeIdForm): NodeIdT {
  if (node.identifierKind === "string") {
    return new NodeIdT(
      NodeIdentifier.StringNodeId,
      new StringNodeIdT(node.namespaceIndex, node.identifier),
    );
  }
  if (node.identifierKind === "guid") {
    return new NodeIdT(
      NodeIdentifier.GuidNodeId,
      new GuidNodeIdT(node.namespaceIndex, node.identifier),
    );
  }
  if (node.identifierKind === "bytestring") {
    return new NodeIdT(
      NodeIdentifier.ByteStringNodeId,
      new ByteStringNodeIdT(node.namespaceIndex, parseByteList(node.identifier)),
    );
  }
  if (node.identifierKind === "browsepath") {
    return new NodeIdT(
      NodeIdentifier.BrowsePath,
      new BrowsePathT(
        node.startingNode
          ? editorNodeIdToFb(node.startingNode)
          : editorNodeIdToFb(BLANK_NUMERIC_NODE),
        (node.hops ?? []).map(editorHopToFb),
      ),
    );
  }
  const numericIdentifier = Number(node.identifier);
  return new NodeIdT(
    NodeIdentifier.NumericNodeId,
    new NumericNodeIdT(
      node.namespaceIndex,
      Number.isFinite(numericIdentifier) ? numericIdentifier : 0,
    ),
  );
}

const BLANK_NUMERIC_NODE: OpcUaNodeIdForm = {
  namespaceIndex: 0,
  identifierKind: "numeric",
  identifier: "0",
};

function fbHopToEditor(hop: RelativePathElementT): OpcUaRelativePathHopForm {
  return {
    referenceTypeNode: hop.referenceTypeId ? fbNodeIdToEditor(hop.referenceTypeId) : null,
    isInverse: hop.isInverse,
    includeSubtypes: hop.includeSubtypes,
    targetNamespaceIndex: hop.targetName?.namespaceIndex ?? 0,
    targetName: asDecodedString(hop.targetName?.name ?? null),
  };
}

/** Convert a NodeIdT back to an OpcUaNodeIdForm — dispatches across all 5 NodeIdentifier kinds. */
function fbNodeIdToEditor(nodeId: NodeIdT | null): OpcUaNodeIdForm {
  if (nodeId?.idType === NodeIdentifier.StringNodeId && nodeId.id) {
    const id = nodeId.id as StringNodeIdT;
    return {
      namespaceIndex: id.namespaceIndex,
      identifierKind: "string",
      identifier: asDecodedString(id.identifier),
    };
  }
  if (nodeId?.idType === NodeIdentifier.GuidNodeId && nodeId.id) {
    const id = nodeId.id as GuidNodeIdT;
    return {
      namespaceIndex: id.namespaceIndex,
      identifierKind: "guid",
      identifier: asDecodedString(id.identifier),
    };
  }
  if (nodeId?.idType === NodeIdentifier.ByteStringNodeId && nodeId.id) {
    const id = nodeId.id as ByteStringNodeIdT;
    return {
      namespaceIndex: id.namespaceIndex,
      identifierKind: "bytestring",
      identifier: formatByteList(id.identifier),
    };
  }
  if (nodeId?.idType === NodeIdentifier.BrowsePath && nodeId.id) {
    const id = nodeId.id as BrowsePathT;
    return {
      namespaceIndex: 0,
      identifierKind: "browsepath",
      identifier: "",
      startingNode: id.startingNode ? fbNodeIdToEditor(id.startingNode) : null,
      hops: id.elements.map(fbHopToEditor),
    };
  }
  if (nodeId?.idType === NodeIdentifier.NumericNodeId && nodeId.id) {
    const id = nodeId.id as NumericNodeIdT;
    return {
      namespaceIndex: id.namespaceIndex,
      identifierKind: "numeric",
      identifier: `${id.identifier}`,
    };
  }
  return { namespaceIndex: 0, identifierKind: "numeric", identifier: "0" };
}

// =====================================================================
// Data/Variant <-> Editor (22-kind Data union, shared by node values,
// method arguments, and literal filter operands)
// =====================================================================

type DataValue = DataHolderT["value"];

/**
 * The NodeId a "nodeid" / "expandednodeid" value form carries. The full
 * `nodeId` wins; a form that only has the numeric `ns;identifier` display
 * string (built by hand or by an older caller) falls back to that.
 */
function nodeIdOfValueForm(form: OpcUaDataValueForm): OpcUaNodeIdForm {
  if (form.nodeId) return form.nodeId;
  const { namespaceIndex, identifier } = parseNsIdentifier(form.value);
  return { namespaceIndex, identifierKind: "numeric", identifier };
}

/** Build the concrete Data-union member `*T` for a given kind + string value. Dispatch table keyed by kind (no nested ternary). */
const DATA_BUILDERS: Record<OpcUaDataKind, (form: OpcUaDataValueForm) => DataValue> = {
  bool: (f) => new BoolT(f.value === "true" || f.value === "1"),
  sbyte: (f) => new SByteT(Number(f.value) || 0),
  byte: (f) => new ByteT(Number(f.value) || 0),
  u16: (f) => new U16T(Number(f.value) || 0),
  i16: (f) => new I16T(Number(f.value) || 0),
  u32: (f) => new U32T(Number(f.value) || 0),
  i32: (f) => new I32T(Number(f.value) || 0),
  u64: (f) => new U64T(BigInt(f.value || "0")),
  i64: (f) => new I64T(BigInt(f.value || "0")),
  float: (f) => new FloatT(Number(f.value) || 0),
  double: (f) => new DoubleT(Number(f.value) || 0),
  string: (f) => new StringT(f.value),
  datetime: (f) => new DateTimeT(BigInt(f.value || "0")),
  guid: (f) => new GuidT(f.value),
  bytestring: (f) => new ByteStringT(parseByteList(f.value)),
  statuscode: (f) => new StatusCodeValueT(Number(f.value) || 0),
  localizedtext: (f) => {
    // Encoded as "locale;text"; a value without a ";" is text-only (no locale).
    const [locale, ...rest] = f.value.split(";");
    return rest.length > 0
      ? new LocalizedTextT(locale, rest.join(";"))
      : new LocalizedTextT("", f.value);
  },
  qualifiedname: (f) => {
    const { namespaceIndex, identifier } = parseNsIdentifier(f.value);
    return new QualifiedNameValueT(namespaceIndex, identifier);
  },
  nodeid: (f) => new NodeIdValueT(editorNodeIdToFb(nodeIdOfValueForm(f))),
  expandednodeid: (f) =>
    new ExpandedNodeIdValueT(
      editorNodeIdToFb(nodeIdOfValueForm(f)),
      f.namespaceUri || null,
      f.serverIndex ?? 0,
    ),
  xmlelement: (f) => new XmlElementT(f.value),
  array: (f) => new ArrayT((f.elements ?? []).map(editorDataValueToHolder)),
};

const DATA_KIND_TO_ENUM: Record<OpcUaDataKind, Data> = {
  bool: Data.Bool,
  sbyte: Data.SByte,
  byte: Data.Byte,
  u16: Data.U16,
  i16: Data.I16,
  u32: Data.U32,
  i32: Data.I32,
  u64: Data.U64,
  i64: Data.I64,
  float: Data.Float,
  double: Data.Double,
  string: Data.String,
  datetime: Data.DateTime,
  guid: Data.Guid,
  bytestring: Data.ByteString,
  statuscode: Data.StatusCodeValue,
  localizedtext: Data.LocalizedText,
  qualifiedname: Data.QualifiedNameValue,
  nodeid: Data.NodeIdValue,
  expandednodeid: Data.ExpandedNodeIdValue,
  xmlelement: Data.XmlElement,
  array: Data.Array,
};

/** Build a DataHolderT (valueType + value pair) from an OpcUaDataValueForm. */
function editorDataValueToHolder(form: OpcUaDataValueForm): DataHolderT {
  const valueType = DATA_KIND_TO_ENUM[form.kind];
  return new DataHolderT(valueType, DATA_BUILDERS[form.kind](form));
}

const DATA_ENUM_TO_KIND: Partial<Record<Data, OpcUaDataKind>> = {
  [Data.Bool]: "bool",
  [Data.SByte]: "sbyte",
  [Data.Byte]: "byte",
  [Data.U16]: "u16",
  [Data.I16]: "i16",
  [Data.U32]: "u32",
  [Data.I32]: "i32",
  [Data.U64]: "u64",
  [Data.I64]: "i64",
  [Data.Float]: "float",
  [Data.Double]: "double",
  [Data.String]: "string",
  [Data.DateTime]: "datetime",
  [Data.Guid]: "guid",
  [Data.ByteString]: "bytestring",
  [Data.StatusCodeValue]: "statuscode",
  [Data.LocalizedText]: "localizedtext",
  [Data.QualifiedNameValue]: "qualifiedname",
  [Data.NodeIdValue]: "nodeid",
  [Data.ExpandedNodeIdValue]: "expandednodeid",
  [Data.XmlElement]: "xmlelement",
  [Data.Array]: "array",
};

/** Extract the string form of a decoded Data-union value for every kind except Array. Dispatch by kind (no nested ternary). */
const DATA_STRINGIFIERS: Partial<Record<OpcUaDataKind, (value: DataValue) => string>> = {
  bool: (v) => `${(v as BoolT).value}`,
  sbyte: (v) => `${(v as SByteT).value}`,
  byte: (v) => `${(v as ByteT).value}`,
  u16: (v) => `${(v as U16T).value}`,
  i16: (v) => `${(v as I16T).value}`,
  u32: (v) => `${(v as U32T).value}`,
  i32: (v) => `${(v as I32T).value}`,
  u64: (v) => `${(v as U64T).value}`,
  i64: (v) => `${(v as I64T).value}`,
  float: (v) => `${(v as FloatT).value}`,
  double: (v) => `${(v as DoubleT).value}`,
  string: (v) => asDecodedString((v as StringT).value),
  datetime: (v) => `${(v as DateTimeT).value}`,
  guid: (v) => asDecodedString((v as GuidT).value),
  bytestring: (v) => formatByteList((v as ByteStringT).value),
  statuscode: (v) => `${(v as StatusCodeValueT).value}`,
  localizedtext: (v) => {
    const t = v as LocalizedTextT;
    const locale = asDecodedString(t.locale);
    const text = asDecodedString(t.text);
    // Mirror the builder: omit the "locale;" prefix when there is no locale.
    return locale ? `${locale};${text}` : text;
  },
  qualifiedname: (v) => {
    const t = v as QualifiedNameValueT;
    return formatNsIdentifier(t.namespaceIndex, asDecodedString(t.name));
  },
  nodeid: (v) => {
    const t = (v as NodeIdValueT).nodeId;
    const asForm = fbNodeIdToEditor(t);
    return formatNsIdentifier(asForm.namespaceIndex, asForm.identifier);
  },
  expandednodeid: (v) => {
    const t = (v as ExpandedNodeIdValueT).nodeId;
    const asForm = fbNodeIdToEditor(t);
    return formatNsIdentifier(asForm.namespaceIndex, asForm.identifier);
  },
  xmlelement: (v) => asDecodedString((v as XmlElementT).value),
};

/** Decode a DataHolderT back to an OpcUaDataValueForm. Unknown/NONE decodes as a blank string kind. */
function fbDataHolderToEditor(holder: DataHolderT | null): OpcUaDataValueForm {
  if (!holder || !holder.value) return { kind: "string", value: "" };
  const kind = DATA_ENUM_TO_KIND[holder.valueType];
  if (!kind) return { kind: "string", value: "" };
  if (kind === "array") {
    const arr = holder.value as ArrayT;
    return { kind: "array", value: "", elements: arr.elements.map(fbDataHolderToEditor) };
  }
  const stringify = DATA_STRINGIFIERS[kind];
  const value = stringify ? stringify(holder.value) : "";
  if (kind === "nodeid") {
    return { kind, value, nodeId: fbNodeIdToEditor((holder.value as NodeIdValueT).nodeId) };
  }
  if (kind === "expandednodeid") {
    const expanded = holder.value as ExpandedNodeIdValueT;
    return {
      kind,
      value,
      nodeId: fbNodeIdToEditor(expanded.nodeId),
      namespaceUri: asDecodedString(expanded.namespaceUri),
      serverIndex: expanded.serverIndex,
    };
  }
  return { kind, value };
}

/** Convert a NodeId + a foundation node-value string/typed-value form into a NodeValueT. */
function editorNodeValueToFb(
  node: OpcUaNodeIdForm,
  value: OpcUaDataValueForm,
  attributeId: AttributeId,
): NodeValueT {
  const holder = editorDataValueToHolder(value);
  return new NodeValueT(editorNodeIdToFb(node), holder.valueType, holder.value, attributeId);
}

/** Extract the typed OpcUaDataValueForm from a NodeValueT. */
function fbNodeValueToEditorForm(value: NodeValueT | null): OpcUaDataValueForm {
  if (!value) return { kind: "string", value: "" };
  return fbDataHolderToEditor(new DataHolderT(value.valueType, value.value));
}

// =====================================================================
// Identity / Certificate
// =====================================================================

/** Convert the discriminated-union identity form to the generated Identity union pair. */
function editorIdentityToFb(identity: OpcUaIdentityForm): {
  identityType: Identity;
  identityValue: AnonymousIdentityT | UserNameIdentityT | X509IdentityT;
} {
  if (identity.kind === "username") {
    return {
      identityType: Identity.UserNameIdentity,
      identityValue: new UserNameIdentityT(identity.username, identity.password),
    };
  }
  if (identity.kind === "x509") {
    return {
      identityType: Identity.X509Identity,
      identityValue: new X509IdentityT(identity.certificatePath, identity.privateKeyPath),
    };
  }
  return { identityType: Identity.AnonymousIdentity, identityValue: new AnonymousIdentityT() };
}

/** Convert an optional certificate form to a CertificateConfigT, or null when unset. */
function editorCertificateToFb(cert: OpcUaCertificateConfigForm | null): CertificateConfigT | null {
  if (!cert) return null;
  return new CertificateConfigT(
    cert.applicationCertPath,
    cert.privateKeyPath,
    cert.trustListDir,
    cert.rejectListDir,
    cert.trustServerCertificate,
    cert.createKeypairIfMissing,
    cert.verifyServerHostname,
  );
}

/** Convert an OpcUaNodeToMessageRow to a NodeValueToMessageEntryT, or null when the row is incomplete. */
/**
 * The `{ message, valueType, value }` leaf inside a built LN2 message, if it
 * has one.
 *
 * Each category nests differently — Status goes MessageT -> BitMaskMessageT ->
 * StatusMessageT -> ElevatorStatusMessageT -> ValuedStatusT, Control and Fault
 * differ in depth — so this descends rather than hard-coding a path. The three
 * fields together identify the leaf unambiguously: only a Valued* table carries
 * an enum `message` alongside a `valueType`/`value` union pair. A message built
 * from a `:normal:` key has no such leaf and yields null.
 */
function findValuedLeaf(node: unknown): { valueType: Value; value: unknown } | null {
  if (node === null || typeof node !== "object") return null;
  const obj = node as Record<string, unknown>;
  if ("valueType" in obj && "value" in obj && "message" in obj) {
    return obj as unknown as { valueType: Value; value: unknown };
  }
  return Object.keys(obj).reduce<{ valueType: Value; value: unknown } | null>(
    (found, key) => found ?? findValuedLeaf(obj[key]),
    null,
  );
}

/**
 * Give a node->message mapping's LN2 message the payload VTS will emit for it.
 *
 * WHY THIS EXISTS
 *
 * VTS resolves an inbound OPC UA read by keyed lookup on (node, observed value)
 * and forwards the mapped message from the CDO **verbatim** — it does not splice
 * the observed value in afterwards. So whatever payload the CDO stores is the
 * payload that reaches LN2.
 *
 * `compositeKeyToValuedMessageT` builds every valued leaf as
 * `(enum, Value.NONE, null)`, because a composite key encodes only
 * category/variant/enum and has nowhere to put a value. The editor has no field
 * for one either. The result was that any OPC UA mapping authored through the UI
 * emitted `ValuedStatus { message: Power, value: NONE }` — a *valued* message
 * with nothing in it, which is what `demos/opcua_e2e`'s end-to-end value-fidelity
 * check caught. The demo had been passing only because its fixture hand-authored
 * the FlatBuffer tree and could embed a value the product could not.
 *
 * The trigger value is the right payload: an entry means "when this node reads
 * X, report this message", and the message is about X. It is also the only value
 * in the row, and it reproduces byte-for-byte what the hand-authored fixture
 * carried.
 *
 * Numeric kinds only, and `:normal:` keys are left alone — a NormalStatus has no
 * value slot, and a non-numeric trigger (a string or GUID node) has no numeric
 * LN2 representation, so those keep the previous behaviour rather than guessing.
 */
function attachLn2PayloadFromTrigger(message: ValuedMessageT, trigger: OpcUaDataValueForm): void {
  const leaf = findValuedLeaf(message);
  if (!leaf || leaf.valueType !== Value.NONE) return;

  const numeric = Number(trigger.value);
  if (!Number.isFinite(numeric)) return;

  leaf.valueType = Value.IntValue;
  leaf.value = new IntValueT(numeric);
}

/**
 * Stamp a mapping row's floor onto the LN2 message VTS will emit for it.
 *
 * The mirror of `channelModifierPayloadBuilder`'s `extractPositionFromPayload`,
 * and deliberately shaped arm-for-arm like it: the elevator arms of Traffic and
 * Control are the only messages with a `position` field, and the escalator
 * variants have none. If the two drift, an authored floor and a read-back floor
 * stop agreeing, which is the failure this pair exists to prevent.
 *
 * WHY THIS EXISTS
 *
 * `compositeKeyToValuedMessageT` builds `ElevatorTrafficMessageT` with a literal
 * 0 for position, because a composite key encodes only category/variant/enum.
 * VTS forwards the mapped message verbatim (`opc_ua_protocol::conversion`
 * `to_liftnet`), and the monitoring decoder reads a car call, a hall call and
 * the car's floor off `position` — never off the valued leaf's `value`. So every
 * position-bearing OPC UA mapping the editor could author addressed floor 0: the
 * call never lit, and nothing reported it. Outbound is the same lookup with the
 * floor inside the key, so a commanded "car call, floor 3" found no entry.
 *
 * A null position leaves the message untouched. For Status, Usage, Service and
 * Fault that is the only correct outcome: they have no `position` field, so a
 * floor on such a row is dropped here rather than invented. For Traffic and
 * Control a null cannot mean "no floor", because `position` is a uint16 and the
 * message already holds its default of 0; that row ships as floor 0. The editor
 * therefore requires a floor for those messages (`messageCarriesPosition`) and
 * defaults it to 0, so the value on the wire is always one the operator saw.
 */
function attachLn2PositionFromRow(message: ValuedMessageT, position: number | null): void {
  if (position === null) return;

  const wrapper: BitMaskMessageT | OneHotMessageT | null | undefined = message.msg?.content;
  if (!wrapper?.message) return;

  if (wrapper.messageType === MessageContent.Traffic_TrafficMessage) {
    const traffic = wrapper.message as TrafficMessageT;
    if (traffic.messageType !== DeviceTrafficMessage.ElevatorTrafficMessage) return;
    (traffic.message as ElevatorTrafficMessageT).position = position;
    return;
  }

  if (wrapper.messageType === MessageContent.Control_ControlMessage) {
    const control = wrapper.message as ControlMessageT;
    if (control.controlType !== DeviceControlMessage.ElevatorControlMessage) return;
    (control.control as ElevatorControlMessageT).position = position;
  }
}

/**
 * The generated mode a form mode packs to.
 *
 * Spelled as a table keyed by the form's own union so a mode added to
 * `OpcUaFloorExpansionMode` without an entry here fails to compile, rather than
 * packing as `None_` and having LiftCloud drop the template.
 */
const FLOOR_EXPANSION_MODE_TO_FB: Record<OpcUaFloorExpansionForm["mode"], FloorExpansionMode> = {
  dataOnly: FloorExpansionMode.DataOnly,
  pathOnly: FloorExpansionMode.PathOnly,
  pathAndData: FloorExpansionMode.PathAndData,
};

function editorFloorExpansionToFb(expansion: OpcUaFloorExpansionForm): FloorExpansionT {
  return new FloorExpansionT(
    FLOOR_EXPANSION_MODE_TO_FB[expansion.mode],
    expansion.valueBase,
    expansion.pathBase,
  );
}

/**
 * The form expansion a generated one decodes to, or null for a mode the editor
 * cannot author: `None_` (an entry that expands nothing) and any value a newer
 * schema adds. Such a template is skipped rather than shown as a different one.
 */
function fbFloorExpansionToEditor(
  expansion: FloorExpansionT | null,
): OpcUaFloorExpansionForm | null {
  if (!expansion) return null;
  switch (expansion.mode) {
    case FloorExpansionMode.DataOnly:
      return { mode: "dataOnly", valueBase: expansion.valueBase, pathBase: expansion.pathBase };
    case FloorExpansionMode.PathOnly:
      return { mode: "pathOnly", valueBase: expansion.valueBase, pathBase: expansion.pathBase };
    case FloorExpansionMode.PathAndData:
      return { mode: "pathAndData", valueBase: expansion.valueBase, pathBase: expansion.pathBase };
    default:
      return null;
  }
}

/**
 * True when two NodeIds address the same node. A BrowsePath never matches: its
 * `identifier` is always empty, so equal text there says nothing.
 */
function sameNodeId(a: OpcUaNodeIdForm, b: OpcUaNodeIdForm): boolean {
  return (
    a.identifierKind !== "browsepath" &&
    a.identifierKind === b.identifierKind &&
    a.namespaceIndex === b.namespaceIndex &&
    a.identifier === b.identifier
  );
}

/**
 * Whether a mapping row is a floor template.
 *
 * A row from before the field existed (an editor state persisted earlier, a
 * fixture built by hand) has no `floorExpansion` at all. That reads as "not a
 * template", the same as `null`; comparing against `null` alone would pack it as
 * a template with no expansion and fail on the first mode lookup.
 */
function isFloorTemplate(row: {
  floorExpansion?: OpcUaFloorExpansionForm | null;
}): row is { floorExpansion: OpcUaFloorExpansionForm } {
  return row.floorExpansion !== null && row.floorExpansion !== undefined;
}

/** True when a template's expansion substitutes `{floor}` in the node identifier. */
function expandsNodePath(expansion: OpcUaFloorExpansionForm): boolean {
  return expansion.mode === "pathOnly" || expansion.mode === "pathAndData";
}

function editorNodeToMsgRowToFb(row: OpcUaNodeToMessageRow): NodeValueToMessageEntryT | null {
  const value = row.msgId ? compositeKeyToValuedMessageT(row.msgId, row.active) : null;
  if (!value) return null;
  attachLn2PayloadFromTrigger(value, row.nodeValue);
  // A template row carries no floor of its own: LiftCloud stamps floor `i` on
  // every entry it expands, so the message keeps the position 0 the composite
  // key built.
  if (!isFloorTemplate(row)) attachLn2PositionFromRow(value, row.position);
  return new NodeValueToMessageEntryT(
    editorNodeValueToFb(row.node, row.nodeValue, row.attributeId),
    value,
  );
}

/** Convert an OpcUaMessageToNodeRow to a MessageToNodeValueEntryT, or null when the row is incomplete. */
function editorMsgToNodeRowToFb(row: OpcUaMessageToNodeRow): MessageToNodeValueEntryT | null {
  const key = row.msgId ? compositeKeyToValuedMessageT(row.msgId, row.active) : null;
  if (!key) return null;
  if (!isFloorTemplate(row)) attachLn2PositionFromRow(key, row.position);
  return new MessageToNodeValueEntryT(
    key,
    editorNodeValueToFb(row.node, row.nodeValue, row.attributeId),
  );
}

/**
 * The CDO entry one mapping row packs to, as the Object API tree, or null when
 * the row is incomplete. This is exactly what `editorToOpcUaMappingT` puts in
 * `mappings.nodeToMsg` / `mappings.msgToNode`; the editor shows it so an operator
 * can see how a row ended up in the CDO.
 */
export function opcUaMappingRowToCdoEntry(
  direction: "nodeToMsg" | "msgToNode",
  row: OpcUaNodeToMessageRow | OpcUaMessageToNodeRow,
): NodeValueToMessageEntryT | MessageToNodeValueEntryT | null {
  return direction === "nodeToMsg"
    ? editorNodeToMsgRowToFb(row as OpcUaNodeToMessageRow)
    : editorMsgToNodeRowToFb(row as OpcUaMessageToNodeRow);
}

// =====================================================================
// Session / Redundancy / Subscriptions
// =====================================================================

function editorSessionToFb(session: OpcUaSessionForm | null): SessionConfigT | null {
  if (!session) return null;
  return new SessionConfigT(
    session.sessionTimeoutMs,
    session.secureChannelLifetimeMs,
    session.requestTimeoutMs,
    session.keepAliveIntervalMs,
    session.sessionRetryLimit,
    session.sessionRetryIntervalMs,
    [...session.preferredLocales],
    session.maxChunkCount,
    session.maxMessageSize,
    session.timestampsToReturn,
  );
}

function fbSessionToEditor(session: SessionConfigT | null): OpcUaSessionForm | null {
  if (!session) return null;
  return {
    sessionTimeoutMs: session.sessionTimeoutMs,
    secureChannelLifetimeMs: session.secureChannelLifetimeMs,
    requestTimeoutMs: session.requestTimeoutMs,
    keepAliveIntervalMs: session.keepAliveIntervalMs,
    sessionRetryLimit: session.sessionRetryLimit,
    sessionRetryIntervalMs: session.sessionRetryIntervalMs,
    preferredLocales: session.preferredLocales.map(asDecodedString),
    maxChunkCount: session.maxChunkCount,
    maxMessageSize: session.maxMessageSize,
    timestampsToReturn: session.timestampsToReturn,
  };
}

function editorRedundancyToFb(redundancy: OpcUaRedundancyForm | null): RedundancyConfigT | null {
  if (!redundancy) return null;
  return new RedundancyConfigT(redundancy.mode, BigInt(Math.round(redundancy.failoverTimeoutMs)));
}

function fbRedundancyToEditor(redundancy: RedundancyConfigT | null): OpcUaRedundancyForm | null {
  if (!redundancy) return null;
  return {
    mode: redundancy.mode as RedundancySupport,
    failoverTimeoutMs: Number(redundancy.failoverTimeoutMs),
  };
}

function editorSubscriptionConfigToFb(config: OpcUaSubscriptionConfigForm): SubscriptionConfigT {
  return new SubscriptionConfigT(
    config.publishingIntervalMs,
    config.lifetimeCount,
    config.maxKeepAliveCount,
    config.maxNotificationsPerPublish,
    config.priority,
    config.samplingIntervalMs,
    config.queueSize,
    config.publishingEnabled,
  );
}

function fbSubscriptionConfigToEditor(config: SubscriptionConfigT): OpcUaSubscriptionConfigForm {
  return {
    publishingIntervalMs: config.publishingIntervalMs,
    lifetimeCount: config.lifetimeCount,
    maxKeepAliveCount: config.maxKeepAliveCount,
    maxNotificationsPerPublish: config.maxNotificationsPerPublish,
    priority: config.priority,
    samplingIntervalMs: config.samplingIntervalMs,
    queueSize: config.queueSize,
    publishingEnabled: config.publishingEnabled,
  };
}

function editorNamedSubscriptionToFb(named: OpcUaNamedSubscriptionForm): NamedSubscriptionT {
  return new NamedSubscriptionT(named.name, editorSubscriptionConfigToFb(named.config));
}

function fbNamedSubscriptionToEditor(named: NamedSubscriptionT): OpcUaNamedSubscriptionForm {
  return {
    name: asDecodedString(named.name),
    config: named.config
      ? fbSubscriptionConfigToEditor(named.config)
      : { ...BLANK_SUBSCRIPTION_CONFIG_DEFAULTS },
  };
}

const BLANK_SUBSCRIPTION_CONFIG_DEFAULTS: OpcUaSubscriptionConfigForm = {
  publishingIntervalMs: 1000,
  lifetimeCount: 10,
  maxKeepAliveCount: 30,
  maxNotificationsPerPublish: 0,
  priority: 0,
  samplingIntervalMs: 0,
  queueSize: 1,
  publishingEnabled: true,
};

// =====================================================================
// Monitors (MonitorMode union: Subscribe | Poll)
// =====================================================================

function editorMonitorModeToFb(mode: OpcUaMonitorModeForm): {
  modeType: MonitorMode;
  modeValue: SubscribeModeT | PollModeT;
} {
  if (mode.kind === "poll") {
    return {
      modeType: MonitorMode.PollMode,
      modeValue: new PollModeT(BigInt(Math.round(mode.intervalMs))),
    };
  }
  return {
    modeType: MonitorMode.SubscribeMode,
    modeValue: new SubscribeModeT(mode.subscriptionName),
  };
}

function fbMonitorModeToEditor(
  modeType: MonitorMode,
  mode: SubscribeModeT | PollModeT | null,
): OpcUaMonitorModeForm {
  if (modeType === MonitorMode.PollMode && mode) {
    return { kind: "poll", intervalMs: Number((mode as PollModeT).intervalMs) };
  }
  if (modeType === MonitorMode.SubscribeMode && mode) {
    return {
      kind: "subscribe",
      subscriptionName: asDecodedString((mode as SubscribeModeT).subscriptionName),
    };
  }
  return { kind: "subscribe", subscriptionName: "" };
}

function editorMonitoringParamsToFb(
  params: OpcUaMonitoringParametersForm | null,
): MonitoringParametersT | null {
  if (!params) return null;
  return new MonitoringParametersT(
    params.samplingIntervalMs,
    params.queueSize,
    params.discardOldest,
    params.monitoringMode,
    params.dataChangeTrigger,
    params.deadbandType,
    params.deadbandValue,
  );
}

function fbMonitoringParamsToEditor(
  params: MonitoringParametersT | null,
): OpcUaMonitoringParametersForm | null {
  if (!params) return null;
  return {
    samplingIntervalMs: params.samplingIntervalMs,
    queueSize: params.queueSize,
    discardOldest: params.discardOldest,
    monitoringMode: params.monitoringMode,
    dataChangeTrigger: params.dataChangeTrigger,
    deadbandType: params.deadbandType,
    deadbandValue: params.deadbandValue,
  };
}

function editorNodeMonitorToFb(monitor: OpcUaNodeMonitorForm): NodeMonitorT {
  const { modeType, modeValue } = editorMonitorModeToFb(monitor.mode);
  return new NodeMonitorT(
    editorNodeIdToFb(monitor.node),
    modeType,
    modeValue,
    monitor.attributeId,
    editorMonitoringParamsToFb(monitor.params),
  );
}

function fbNodeMonitorToEditor(monitor: NodeMonitorT): OpcUaNodeMonitorForm {
  return {
    node: fbNodeIdToEditor(monitor.nodeId),
    mode: fbMonitorModeToEditor(monitor.modeType, monitor.mode),
    attributeId: monitor.attributeId,
    params: fbMonitoringParamsToEditor(monitor.params),
  };
}

// =====================================================================
// Methods (MessageToMethodEntry: ValuedMessageT -> MethodCallT)
// =====================================================================

function editorMethodToFb(method: OpcUaMethodMappingForm): MessageToMethodEntryT | null {
  const key = method.msgId ? compositeKeyToValuedMessageT(method.msgId, method.active) : null;
  if (!key) return null;
  const call = new MethodCallT(
    editorNodeIdToFb(method.objectId),
    editorNodeIdToFb(method.methodId),
    method.inputArguments.map(editorDataValueToHolder),
  );
  return new MessageToMethodEntryT(key, call);
}

function fbMethodToEditor(entry: MessageToMethodEntryT): OpcUaMethodMappingForm | null {
  const msgId = entry.key ? valuedMessageTToCompositeKey(entry.key) : null;
  if (msgId === null) return null;
  const call = entry.value;
  return {
    msgId,
    active: entry.key?.active ?? true,
    objectId: fbNodeIdToEditor(call?.objectId ?? null),
    methodId: fbNodeIdToEditor(call?.methodId ?? null),
    inputArguments: (call?.inputArguments ?? []).map(fbDataHolderToEditor),
  };
}

// =====================================================================
// Events (EventMonitor / EventFilter / ContentFilter / FilterOperand)
// =====================================================================

/**
 * A browse-path segment is written `ns:Name` (the OPC UA text form of a
 * QualifiedName) when its namespace index is not 0, plain `Name` otherwise.
 */
const BROWSE_NAME_PATTERN = /^(\d+):(.*)$/;

function browseNameToFb(segment: string): QualifiedNameT {
  const match = BROWSE_NAME_PATTERN.exec(segment);
  return match ? new QualifiedNameT(Number(match[1]), match[2]) : new QualifiedNameT(0, segment);
}

function fbBrowseNameToEditor(qn: QualifiedNameT): string {
  const name = asDecodedString(qn.name);
  // A namespace-0 name that itself looks like `7:x` keeps an explicit `0:` so
  // it is not read back as namespace 7.
  if (qn.namespaceIndex !== 0 || BROWSE_NAME_PATTERN.test(name)) {
    return `${qn.namespaceIndex}:${name}`;
  }
  return name;
}

function editorSimpleAttributeOperandToFb(
  operand: OpcUaSimpleAttributeOperandForm,
): SimpleAttributeOperandT {
  return new SimpleAttributeOperandT(
    operand.typeDefinitionNode ? editorNodeIdToFb(operand.typeDefinitionNode) : null,
    operand.browsePath.map(browseNameToFb),
    operand.attributeId,
    operand.indexRange || null,
  );
}

function fbSimpleAttributeOperandToEditor(
  operand: SimpleAttributeOperandT,
): OpcUaSimpleAttributeOperandForm {
  return {
    typeDefinitionNode: operand.typeDefinitionId
      ? fbNodeIdToEditor(operand.typeDefinitionId)
      : null,
    browsePath: operand.browsePath.map(fbBrowseNameToEditor),
    attributeId: operand.attributeId,
    indexRange: asDecodedString(operand.indexRange),
  };
}

/** Build a FilterOperandHolderT (operandType + operand pair) from the discriminated-union filter-operand form. */
function editorFilterOperandToFb(operand: OpcUaFilterOperandForm): FilterOperandHolderT {
  if (operand.kind === "attribute") {
    return new FilterOperandHolderT(
      FilterOperand.SimpleAttributeOperand,
      editorSimpleAttributeOperandToFb(operand.operand),
    );
  }
  if (operand.kind === "element") {
    return new FilterOperandHolderT(
      FilterOperand.ElementOperand,
      new ElementOperandT(operand.index),
    );
  }
  const holder = editorDataValueToHolder(operand.value);
  return new FilterOperandHolderT(
    FilterOperand.LiteralOperand,
    new LiteralOperandT(holder.valueType, holder.value),
  );
}

/** Decode a FilterOperandHolderT back to the discriminated-union filter-operand form. ElementOperand round-trips read-only (no authoring UI). */
function fbFilterOperandToEditor(holder: FilterOperandHolderT): OpcUaFilterOperandForm {
  if (holder.operandType === FilterOperand.SimpleAttributeOperand && holder.operand) {
    return {
      kind: "attribute",
      operand: fbSimpleAttributeOperandToEditor(holder.operand as SimpleAttributeOperandT),
    };
  }
  if (holder.operandType === FilterOperand.ElementOperand && holder.operand) {
    return { kind: "element", index: (holder.operand as ElementOperandT).index };
  }
  if (holder.operandType === FilterOperand.LiteralOperand && holder.operand) {
    const literal = holder.operand as LiteralOperandT;
    return {
      kind: "literal",
      value: fbDataHolderToEditor(new DataHolderT(literal.valueType, literal.value)),
    };
  }
  return { kind: "literal", value: { kind: "string", value: "" } };
}

function editorContentFilterElementToFb(
  element: OpcUaContentFilterElementForm,
): ContentFilterElementT {
  return new ContentFilterElementT(element.operator, element.operands.map(editorFilterOperandToFb));
}

function fbContentFilterElementToEditor(
  element: ContentFilterElementT,
): OpcUaContentFilterElementForm {
  return { operator: element.operator, operands: element.operands.map(fbFilterOperandToEditor) };
}

function editorEventMonitorToFb(monitor: OpcUaEventMonitorForm): EventMonitorT {
  const filter = new EventFilterT(
    monitor.selectClauses.map(editorSimpleAttributeOperandToFb),
    monitor.whereClause.length > 0
      ? new ContentFilterT(monitor.whereClause.map(editorContentFilterElementToFb))
      : null,
  );
  return new EventMonitorT(
    editorNodeIdToFb(monitor.node),
    filter,
    monitor.subscriptionName,
    monitor.queueSize,
  );
}

function fbEventMonitorToEditor(monitor: EventMonitorT): OpcUaEventMonitorForm {
  const filter = monitor.filter;
  return {
    node: fbNodeIdToEditor(monitor.nodeId),
    selectClauses: (filter?.selectClauses ?? []).map(fbSimpleAttributeOperandToEditor),
    whereClause: (filter?.whereClause?.elements ?? []).map(fbContentFilterElementToEditor),
    subscriptionName: asDecodedString(monitor.subscriptionName),
    queueSize: monitor.queueSize,
  };
}

function editorEventToMsgRowToFb(row: OpcUaEventToMessageRow): EventToMessageEntryT | null {
  const value = row.msgId ? compositeKeyToValuedMessageT(row.msgId, row.active) : null;
  if (!value) return null;
  return new EventToMessageEntryT(editorNodeIdToFb(row.eventTypeNode), value);
}

function fbEventToMsgEntryToEditor(entry: EventToMessageEntryT): OpcUaEventToMessageRow | null {
  const msgId = entry.value ? valuedMessageTToCompositeKey(entry.value) : null;
  if (msgId === null) return null;
  return {
    eventTypeNode: fbNodeIdToEditor(entry.eventTypeId),
    msgId,
    active: entry.value?.active ?? true,
  };
}

// =====================================================================
// History Reads
// =====================================================================

function editorHistoryReadToFb(read: OpcUaHistoryReadForm): HistoryReadConfigT {
  return new HistoryReadConfigT(
    editorNodeIdToFb(read.node),
    read.kind,
    read.startTime || null,
    read.endTime || null,
    read.numValuesPerNode,
    read.returnBounds,
    read.aggregateTypeNode ? editorNodeIdToFb(read.aggregateTypeNode) : null,
    read.processingIntervalMs,
  );
}

function fbHistoryReadToEditor(read: HistoryReadConfigT): OpcUaHistoryReadForm {
  return {
    node: fbNodeIdToEditor(read.nodeId),
    kind: read.kind,
    startTime: asDecodedString(read.startTime),
    endTime: asDecodedString(read.endTime),
    numValuesPerNode: read.numValuesPerNode,
    returnBounds: read.returnBounds,
    aggregateTypeNode: read.aggregateTypeId ? fbNodeIdToEditor(read.aggregateTypeId) : null,
    processingIntervalMs: read.processingIntervalMs,
  };
}

// =====================================================================
// Floor templates (NodeToMsgTemplate / MsgToNodeTemplate)
// =====================================================================

/**
 * The explicit monitor that belongs to a path-axis inbound template: the first
 * one watching the template's node, whose identifier still holds `{floor}`.
 * LiftCloud expands the template's monitor per floor along with its entry, so
 * the monitor lives inside the template rather than in the top-level list.
 */
function monitorForTemplate(
  row: OpcUaNodeToMessageRow,
  monitors: readonly OpcUaNodeMonitorForm[],
): OpcUaNodeMonitorForm | null {
  if (!isFloorTemplate(row) || !expandsNodePath(row.floorExpansion)) return null;
  if (!row.node.identifier.includes(FLOOR_PLACEHOLDER)) return null;
  return monitors.find((m) => sameNodeId(m.node, row.node)) ?? null;
}

/**
 * Every inbound template row packed to its generated table, with the monitors
 * that were written inside a template so the caller leaves them out of the
 * top-level `monitors`. A path-axis template with no such monitor writes none.
 */
function editorNodeToMsgTemplatesToFb(
  rows: readonly OpcUaNodeToMessageRow[],
  monitors: readonly OpcUaNodeMonitorForm[],
): { nodeToMsgTemplates: NodeToMsgTemplateT[]; pairedMonitors: ReadonlySet<OpcUaNodeMonitorForm> } {
  const pairedMonitors = new Set<OpcUaNodeMonitorForm>();
  const nodeToMsgTemplates: NodeToMsgTemplateT[] = [];
  rows.forEach((row) => {
    if (!isFloorTemplate(row)) return;
    const entry = editorNodeToMsgRowToFb(row);
    if (entry === null) return;
    const monitor = monitorForTemplate(row, monitors);
    if (monitor !== null) pairedMonitors.add(monitor);
    nodeToMsgTemplates.push(
      new NodeToMsgTemplateT(
        entry,
        editorFloorExpansionToFb(row.floorExpansion),
        monitor === null ? null : editorNodeMonitorToFb(monitor),
      ),
    );
  });
  return { nodeToMsgTemplates, pairedMonitors };
}

function editorMsgToNodeTemplateToFb(row: OpcUaMessageToNodeRow): MsgToNodeTemplateT | null {
  if (!isFloorTemplate(row)) return null;
  const entry = editorMsgToNodeRowToFb(row);
  if (entry === null) return null;
  return new MsgToNodeTemplateT(entry, editorFloorExpansionToFb(row.floorExpansion));
}

// =====================================================================
// Editor -> FlatBuffer (top-level Mapping)
// =====================================================================

/**
 * Convert the UI editor state into a FlatBuffer Object API `OpcUa.MappingT`
 * ready for packing into binary.
 */
export function editorToOpcUaMappingT(editor: AdapterCdoOpcUa): MappingT {
  const { identityType, identityValue } = editorIdentityToFb(editor.identity);

  // Template rows go ONLY to the template vectors, never to the literal ones: an
  // entry in both would be matched once as a literal and expanded again by
  // LiftCloud, which then drops the duplicate floor.
  const { nodeToMsgTemplates, pairedMonitors } = editorNodeToMsgTemplatesToFb(
    editor.nodeToMsg,
    editor.monitors,
  );
  const msgToNodeTemplates = editor.msgToNode
    .map(editorMsgToNodeTemplateToFb)
    .filter((e): e is MsgToNodeTemplateT => e !== null);

  const mappings = new OpcUaMappingsT(
    editor.nodeToMsg
      .filter((row) => !isFloorTemplate(row))
      .map(editorNodeToMsgRowToFb)
      .filter((e): e is NodeValueToMessageEntryT => e !== null),
    editor.msgToNode
      .filter((row) => !isFloorTemplate(row))
      .map(editorMsgToNodeRowToFb)
      .filter((e): e is MessageToNodeValueEntryT => e !== null),
    nodeToMsgTemplates,
    msgToNodeTemplates,
  );

  return new MappingT(
    editor.applicationName || null,
    editor.applicationUri || null,
    editor.productUri || null,
    editor.securityPolicy,
    editor.securityMode,
    identityType,
    identityValue,
    editorCertificateToFb(editor.certificate),
    editorSessionToFb(editor.session),
    editorRedundancyToFb(editor.redundancy),
    editor.subscription ? editorSubscriptionConfigToFb(editor.subscription) : null,
    editor.namedSubscriptions.map(editorNamedSubscriptionToFb),
    editor.monitors.filter((m) => !pairedMonitors.has(m)).map(editorNodeMonitorToFb),
    mappings,
    // node -> lift binding is authored per-bank now (BankNodeLiftMatrix /
    // Bank.opc_ua_node_lift_mapping_binary, a packed
    // Config.OpcUaNodeLiftIndexBindings FlatBuffer), not on the adapter. The
    // field still exists on the wire (deprecated in place): this editor never
    // authors it, but whatever a loaded adapter held is written back.
    editor.nodeToLiftId ?? [],
    editor.methods.map(editorMethodToFb).filter((e): e is MessageToMethodEntryT => e !== null),
    editor.eventMonitors.map(editorEventMonitorToFb),
    editor.eventMappings
      .map(editorEventToMsgRowToFb)
      .filter((e): e is EventToMessageEntryT => e !== null),
    editor.historyReads.map(editorHistoryReadToFb),
    editor.vendorPathLiteral || null,
  );
}

/** Pack the editor state directly into FlatBuffer binary bytes. */
export function buildOpcUaMappingBytes(editor: AdapterCdoOpcUa): Uint8Array {
  const mappingT = editorToOpcUaMappingT(editor);
  const builder = new flatbuffers.Builder(1024);
  builder.finish(mappingT.pack(builder));
  return builder.asUint8Array();
}

/**
 * Base64-encode packed OPC UA mapping bytes for the `cdo_binary` request
 * field. Mirrors the byte->binary-string->btoa idiom in
 * `AdaptersTableActions.tsx` (adapter export flow).
 */
export function opcUaMappingBytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// =====================================================================
// FlatBuffer -> Editor
// =====================================================================

/** Convert the generated Identity union pair back to the discriminated-union form. */
function fbIdentityToEditor(
  identityType: Identity,
  identity: AnonymousIdentityT | UserNameIdentityT | X509IdentityT | null,
): OpcUaIdentityForm {
  if (identityType === Identity.UserNameIdentity && identity) {
    const userIdentity = identity as UserNameIdentityT;
    return {
      kind: "username",
      username: asDecodedString(userIdentity.username),
      password: asDecodedString(userIdentity.password),
    };
  }
  if (identityType === Identity.X509Identity && identity) {
    const x509Identity = identity as X509IdentityT;
    return {
      kind: "x509",
      certificatePath: asDecodedString(x509Identity.certificatePath),
      privateKeyPath: asDecodedString(x509Identity.privateKeyPath),
    };
  }
  return { kind: "anonymous" };
}

/** Convert a generated CertificateConfigT back to the form shape, or null when absent. */
function fbCertificateToEditor(cert: CertificateConfigT | null): OpcUaCertificateConfigForm | null {
  if (!cert) return null;
  return {
    applicationCertPath: asDecodedString(cert.applicationCertPath),
    privateKeyPath: asDecodedString(cert.privateKeyPath),
    trustListDir: asDecodedString(cert.trustListDir),
    rejectListDir: asDecodedString(cert.rejectListDir),
    trustServerCertificate: cert.trustServerCertificate,
    createKeypairIfMissing: cert.createKeypairIfMissing,
    verifyServerHostname: cert.verifyServerHostname,
  };
}

/** Convert a NodeValueToMessageEntryT back to an OpcUaNodeToMessageRow. */
function fbNodeToMsgEntryToEditor(entry: NodeValueToMessageEntryT): OpcUaNodeToMessageRow {
  const msgId = entry.value ? valuedMessageTToCompositeKey(entry.value) : null;
  return {
    node: fbNodeIdToEditor(entry.key?.nodeId ?? null),
    nodeValue: fbNodeValueToEditorForm(entry.key),
    attributeId: entry.key?.attributeId ?? AttributeId.Value,
    msgId: msgId ?? "",
    active: entry.value?.active ?? true,
    position: extractPositionFromPayload(entry.value?.msg ?? null),
    floorExpansion: null,
  };
}

/** Convert a MessageToNodeValueEntryT back to an OpcUaMessageToNodeRow. */
function fbMsgToNodeEntryToEditor(entry: MessageToNodeValueEntryT): OpcUaMessageToNodeRow {
  const msgId = entry.key ? valuedMessageTToCompositeKey(entry.key) : null;
  return {
    msgId: msgId ?? "",
    active: entry.key?.active ?? true,
    node: fbNodeIdToEditor(entry.value?.nodeId ?? null),
    nodeValue: fbNodeValueToEditorForm(entry.value),
    attributeId: entry.value?.attributeId ?? AttributeId.Value,
    position: extractPositionFromPayload(entry.key?.msg ?? null),
    floorExpansion: null,
  };
}

/**
 * Decode an inbound floor template. Its row has no floor of its own
 * (`position: null`): LiftCloud sets the floor on each expanded entry, so the
 * position the template's message carries (0) is not an authored value. Null
 * when the template's mode is one the editor cannot author.
 */
function fbNodeToMsgTemplateToEditor(template: NodeToMsgTemplateT): OpcUaNodeToMessageRow | null {
  const floorExpansion = fbFloorExpansionToEditor(template.expansion);
  if (floorExpansion === null || template.entry === null) return null;
  return { ...fbNodeToMsgEntryToEditor(template.entry), position: null, floorExpansion };
}

/** Decode an outbound floor template; see `fbNodeToMsgTemplateToEditor`. */
function fbMsgToNodeTemplateToEditor(template: MsgToNodeTemplateT): OpcUaMessageToNodeRow | null {
  const floorExpansion = fbFloorExpansionToEditor(template.expansion);
  if (floorExpansion === null || template.entry === null) return null;
  return { ...fbMsgToNodeEntryToEditor(template.entry), position: null, floorExpansion };
}

/**
 * The editor's monitor list: the top-level monitors, then each inbound
 * template's own monitor unless one for the same node is already listed. That
 * is the inverse of `editorNodeToMsgTemplatesToFb`, so an explicit monitor
 * moved into a template on save comes back as one list entry, not two.
 */
function fbMonitorsToEditor(
  monitors: readonly NodeMonitorT[],
  templates: readonly NodeToMsgTemplateT[],
): OpcUaNodeMonitorForm[] {
  const decoded = monitors.map(fbNodeMonitorToEditor);
  templates.forEach((template) => {
    if (!template.monitor || fbFloorExpansionToEditor(template.expansion) === null) return;
    const monitor = fbNodeMonitorToEditor(template.monitor);
    if (!decoded.some((m) => sameNodeId(m.node, monitor.node))) decoded.push(monitor);
  });
  return decoded;
}

/** Decode FlatBuffer binary bytes into the UI editor state. */
export function decodeOpcUaMapping(bytes: Uint8Array): AdapterCdoOpcUa {
  const buf = new flatbuffers.ByteBuffer(bytes);
  const fb = Mapping.getRootAsMapping(buf);
  const t = fb.unpack();

  const applicationName = t.applicationName;

  return {
    applicationName: asDecodedString(applicationName),
    applicationUri: asDecodedString(t.applicationUri),
    productUri: asDecodedString(t.productUri),
    securityPolicy: t.securityPolicy,
    securityMode: t.securityMode,
    identity: fbIdentityToEditor(t.identityType, t.identity),
    certificate: fbCertificateToEditor(t.certificate),
    session: fbSessionToEditor(t.session),
    redundancy: fbRedundancyToEditor(t.redundancy),
    subscription: t.subscription ? fbSubscriptionConfigToEditor(t.subscription) : null,
    namedSubscriptions: t.namedSubscriptions.map(fbNamedSubscriptionToEditor),
    monitors: fbMonitorsToEditor(t.monitors, t.mappings?.nodeToMsgTemplates ?? []),
    nodeToMsg: [
      ...(t.mappings?.nodeToMsg ?? []).map(fbNodeToMsgEntryToEditor),
      ...(t.mappings?.nodeToMsgTemplates ?? [])
        .map(fbNodeToMsgTemplateToEditor)
        .filter((row): row is OpcUaNodeToMessageRow => row !== null),
    ],
    msgToNode: [
      ...(t.mappings?.msgToNode ?? []).map(fbMsgToNodeEntryToEditor),
      ...(t.mappings?.msgToNodeTemplates ?? [])
        .map(fbMsgToNodeTemplateToEditor)
        .filter((row): row is OpcUaMessageToNodeRow => row !== null),
    ],
    methods: t.msgToMethod
      .map(fbMethodToEditor)
      .filter((e): e is OpcUaMethodMappingForm => e !== null),
    eventMonitors: t.eventMonitors.map(fbEventMonitorToEditor),
    eventMappings: t.eventToMsg
      .map(fbEventToMsgEntryToEditor)
      .filter((e): e is OpcUaEventToMessageRow => e !== null),
    historyReads: t.historyReads.map(fbHistoryReadToEditor),
    ...(t.nodeToLiftId.length > 0 ? { nodeToLiftId: t.nodeToLiftId } : {}),
    ...(t.vendorPathLiteral ? { vendorPathLiteral: asDecodedString(t.vendorPathLiteral) } : {}),
  };
}
